import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DataLoadErrorRow } from '@app/components/DataLoadError';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { RemoveButton } from '@app/components/RemoveButton';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';
import { useEntity } from '@app/hooks/useEntity';
import { useFilterRestore } from '@app/hooks/useFilterRestore';
import { useRowRestore } from '@app/hooks/useRowRestore';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { TrainingSpecialistStrategic } from '@app/model/TrainingSpecialistStrategic';
import { useTrainingSpecialistStrategicStore } from '@app/store/entityStore';
import {
  ActionList, ActionListItem, AlertVariant, Button, Icon, PageSection, Pagination, PaginationVariant,
  SearchInput, Split, SplitItem, Title, Toolbar, ToolbarContent, ToolbarItem, Tooltip
} from '@patternfly/react-core';
import { PencilAltIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, ThProps, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

/** What comes back when the user returns from a TSS's detail page. */
interface TSSView {
  search: string;
  sortIndex: number | null;
  sortDirection: 'asc' | 'desc' | null;
}

const TSSOverview: React.FunctionComponent = () => {
  const columns = ['Name', 'Region', 'Red Hat Region', 'Email', 'Mobile', 'Country'];

  const navigate = useNavigate();
  const { addNotification } = useNotification();

  // '/tss' would otherwise read as "Tss" in the breadcrumb
  useBreadcrumbLabel('TSS');

  const { items: rows, error, retry } = useEntity(useTrainingSpecialistStrategicStore);
  const removeTSS = useTrainingSpecialistStrategicStore(state => state.delete);

  // The search last typed here, kept only for the trip out to a TSS and back.
  const { restored, keep } = useFilterRestore<TSSView>('tss');
  const [searchInputValue, setSearchInputValue] = React.useState<string>(restored?.search ?? '');
  const { restoredPage, rememberRow, rowProps } = useRowRestore('tss');
  const [page, setPage] = React.useState<number>(restoredPage ?? 1);
  const { perPage, setPerPage } = useTablePreferences('tss', 5);

  // Sorting State
  // Restored along with the search: the page number comes back too, and on a differently
  // sorted list that page holds different rows.
  const [activeSortIndex, setActiveSortIndex] = React.useState<number | null>(restored?.sortIndex ?? null);
  const [activeSortDirection, setActiveSortDirection] = React.useState<'asc' | 'desc' | null>(restored?.sortDirection ?? null);

  React.useEffect(() => {
    keep({ search: searchInputValue, sortIndex: activeSortIndex, sortDirection: activeSortDirection });
  }, [searchInputValue, activeSortIndex, activeSortDirection, keep]);

  // --- Dynamic Filtering & Sorting ---
  const filteredAndSortedRows = React.useMemo<TrainingSpecialistStrategic[]>(() => {
    let result = rows;
    if (searchInputValue) {
      const search = searchInputValue.toLowerCase();
      result = result.filter((row: TrainingSpecialistStrategic) =>
        row.name?.toLowerCase().includes(search) ||
        row.email?.toLowerCase().includes(search)
      );
    }

    if (activeSortIndex !== null && activeSortDirection !== null) {
      result = [...result].sort((a, b) => {
        let aValue: any = '';
        let bValue: any = '';

        switch (activeSortIndex) {
          case 0: aValue = a.name; bValue = b.name; break;
          case 1: aValue = a.region; bValue = b.region; break;
          case 2: aValue = a.redHatRegion?.name; bValue = b.redHatRegion?.name; break;
          case 3: aValue = a.email; bValue = b.email; break;
          case 4: aValue = a.mobileNumber; bValue = b.mobileNumber; break;
          case 5: aValue = a.country?.name; bValue = b.country?.name; break;
        }

        if (aValue === bValue) return 0;
        if (!aValue) return activeSortDirection === 'asc' ? -1 : 1;
        if (!bValue) return activeSortDirection === 'asc' ? 1 : -1;

        const comparison = String(aValue).localeCompare(String(bValue));
        return activeSortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [rows, searchInputValue, activeSortIndex, activeSortDirection]);

  const paginatedRows = React.useMemo<TrainingSpecialistStrategic[]>(() => {
    const startIdx = (page - 1) * perPage;
    return filteredAndSortedRows.slice(startIdx, startIdx + perPage);
  }, [filteredAndSortedRows, page, perPage]);

  const onSort = (_event: React.MouseEvent, index: number, direction: 'asc' | 'desc') => {
    setActiveSortIndex(index);
    setActiveSortDirection(direction);
  };

  const getSortParams = (columnIndex: number): ThProps['sort'] => ({
    sortBy: {
      index: activeSortIndex as number,
      direction: activeSortDirection as 'asc' | 'desc',
      defaultDirection: 'asc'
    },
    onSort,
    columnIndex
  });

  const onRemoveConfirm = async (row: TrainingSpecialistStrategic) => {
    beginLoading();
    try {
      await removeTSS(row.id);
      addNotification('TSS removed successfully', AlertVariant.success);
    } catch (error: any) {
      addNotification('Error removing TSS', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  const renderPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={filteredAndSortedRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => {
        setPerPage(newPerPage);
        setPage(newPage);
      }}
      perPageOptions={[{ title: '10', value: 10 }, { title: '20', value: 20 }, { title: '50', value: 50 }]}
      variant={variant as PaginationVariant}
      titles={{ paginationAriaLabel: `${variant} pagination` }}
    />
  );

  return (
    <React.Fragment>
      <PageSection isWidthLimited>
        <Split hasGutter>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Training Specialist Strategic</Title>
          </SplitItem>
          <SplitItem isFilled />
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <Button variant="primary" id="new-button" onClick={() => navigate('/tss/new')}>New TSS</Button>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      <PageSection isWidthLimited isFilled>
        <Toolbar id="compact-toolbar">
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                aria-label="Search by name or email"
                placeholder="Search name or email..."
                value={searchInputValue}
                onChange={(_event, value) => {
                  setSearchInputValue(value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearchInputValue('');
                  setPage(1);
                }}
              />
            </ToolbarItem>
            <ToolbarItem variant="pagination">{renderPagination('top', true)}</ToolbarItem>
          </ToolbarContent>
        </Toolbar>
        {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
            gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
        <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset))', minHeight: 0 }}>
          <InnerScrollContainer>
            <Table aria-label="Training Specialist Strategic" isStickyHeader variant='compact' gridBreakPoint="">
              <Thead>
                <Tr>
                  <Th sort={getSortParams(0)}>{columns[0]}</Th>
                  <Th sort={getSortParams(1)}>{columns[1]}</Th>
                  <Th sort={getSortParams(2)}>{columns[2]}</Th>
                  <Th sort={getSortParams(3)}>{columns[3]}</Th>
                  <Th sort={getSortParams(4)}>{columns[4]}</Th>
                  <Th sort={getSortParams(5)}>{columns[5]}</Th>
                  <Th isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedRows.map((row) => (
                  <Tr key={row.id} {...rowProps(row.id)}>
                    <Td dataLabel={columns[0]}>{row.name}</Td>
                    <Td dataLabel={columns[1]}>{row.region}</Td>
                    <Td dataLabel={columns[2]}>{row.redHatRegion?.name || 'N/A'}</Td>
                    <Td dataLabel={columns[3]}>{row.email}</Td>
                    <Td dataLabel={columns[4]}>{row.mobileNumber}</Td>
                    <Td dataLabel={columns[5]}>{row.country?.name}</Td>
                    <Td dataLabel="Actions" modifier="nowrap"
                      isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>
                      <ActionList>
                        <ActionListItem>
                          <Tooltip content="Edit">
                            <Button
                              icon={<Icon size='lg'><PencilAltIcon /></Icon>}
                              variant="plain"
                              id="edit-button"
                              onClick={() => { rememberRow(row.id, page); navigate(`/tss/${row.id}`); }}
                            />
                          </Tooltip>
                        </ActionListItem>
                        <ActionListItem>
                          <RemoveButton
                            confirmationMessage={`Confirm removal of ${row.name}?`}
                            onRemoveConfirm={() => onRemoveConfirm(row)}
                          />
                        </ActionListItem>
                      </ActionList>
                    </Td>
                  </Tr>
                ))}
                {/* Nothing to show because the load failed, rather than because the list is empty */}
                {error && paginatedRows.length === 0 && (
                  <DataLoadErrorRow colSpan={7} error={error} entityLabel="training specialists" onRetry={retry} />
                )}
              </Tbody>
            </Table>
          </InnerScrollContainer>
        </OuterScrollContainer>
        {renderPagination('bottom', false)}
      </PageSection>
    </React.Fragment>
  );
};

export { TSSOverview };
